import { useIntl } from 'react-intl'
import {
  Anchor,
  csx,
  IconXCircle,
  Spinner,
  Text,
  Tooltip,
} from '@vtex/admin-ui'
import { useAdmin } from '@vtex/raccoon-next'
import {
  IconArrowLineDown,
  IconButton,
  IconCheckCircleFill,
} from '@vtex/shoreline'
import { type DownloadItemProps } from '@/components/products-listing/components/DownloadCollapser/DownloadListCollapser'
import { useExportRequests } from '@/components/products-listing/hooks/useExportRequest'

import * as styles from './DownloadItem.styles'

interface DownloadItemArray {
  item: DownloadItemProps
}

export const DownloadItem = ({ item }: DownloadItemArray) => {
  const { $t } = useIntl()
  const { locale } = useAdmin()

  const dateObj = new Date(item.CreatedAt)
  const { cancelExportRequest } = useExportRequests({
    exportData: null,
    onCreateExport: () => {},
  })

  const getTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      product: $t({
        id: 'admin/admin.catalog.import-export-menu.products-and-skus-label',
        defaultMessage: 'Products and SKUs',
      }),
      image: $t({
        id: 'admin/admin.catalog.import-export-menu.images-label',
        defaultMessage: 'Images',
      }),
      productspecification: $t({
        id: 'admin/admin.catalog.import-export-menu.product-specifications-label',
        defaultMessage: 'Product Specifications',
      }),
      skuspecification: $t({
        id: 'admin/admin.catalog.import-export-menu.sku-specifications-label',
        defaultMessage: 'SKU Specifications',
      }),
    }

    const result = labels[type] || labels[type.toLowerCase()] || type
    return result
  }

  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }

  const dateTime = dateObj
    .toLocaleString(locale, options)
    .replaceAll('/', '-')
    .replace(',', ' -')

  if (item.Status === 'Cancelled') {
    return
  }

  return (
    <div className={`${styles.container}`} data-status={item.Status}>
      <div className="flex items-center gap-2">
        {item.Status !== 'Completed' ? (
          <Spinner size={18} className={csx({ color: '$secondary' })} />
        ) : (
          <IconCheckCircleFill className={csx({ color: '$positive' })} />
        )}
        <div>
          <Text
            variant="body"
            className={csx({
              display: 'block',
            })}
          >
            {getTypeLabel(item.Type)}
          </Text>
          <Text variant="detail" tone="secondary">
            {dateTime}
          </Text>
        </div>
      </div>
      <div className="flex items-center  gap-2">
        {item.TotalRows !== undefined && (
          <span className={styles.quantityPill}>
            <Text variant="detail">{item.TotalRows}</Text>
          </span>
        )}
        <div className={csx({ minWidth: '44px' })}>
          {item.Status === 'Completed' ? (
            <Tooltip text="Download">
              <Anchor href={item.FileLink || ''}>
                <IconButton label="Download" className={styles.icon}>
                  <IconArrowLineDown />
                </IconButton>
              </Anchor>
            </Tooltip>
          ) : (
            <Tooltip text="Cancel export">
              <IconButton
                label="Cancel export"
                className={styles.icon}
                onClick={() => cancelExportRequest(item.HashLink)}
              >
                <IconXCircle />
              </IconButton>
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  )
}
